/**
 * Follows service — whether the signed-in person follows a record, as far as
 * their own `record_subscriptions` row says. Following a document gets a Slack
 * DM when a note is added to it.
 *
 * A missing row is not "not following": a document's owner and the owners of
 * notes in its thread follow it implicitly, and the notification SQL works that
 * out. A row only records an explicit choice, which beats the implicit one.
 */

import { fetchRecordSubscription, setRecordSubscription } from "../gql/follows.js"
import type { Mutate, Query } from "../index.js"

export function createFollowsService({ query, mutate }: { query: Query; mutate: Mutate }) {
  /** The viewer's explicit choice for a record, or null when they made none. */
  const get = async (recordId: number): Promise<boolean | null> => {
    const data = await query(fetchRecordSubscription, { recordId })
    return data.record_subscriptions[0]?.subscribed ?? null
  }

  /** Follow or unfollow a record explicitly. */
  const set = async (recordId: number, following: boolean): Promise<void> => {
    const data = await mutate(setRecordSubscription, { recordId, subscribed: following })
    if (!data.insert_record_subscriptions_one) {
      throw new Error(`Failed to ${following ? "follow" : "unfollow"} record ${recordId}`)
    }
  }

  return { get, set }
}
